import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Clock3, Gift, HandCoins, Link2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  PaymentIntentDetailDialog,
  type PaymentIntentDetail,
} from '@/components/payments/payment-intent-detail-dialog';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import {
  DateRangeFilter,
  LIST_FILTER_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FilterBar } from '@/components/shared/filter-bar';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { StatCard } from '@/components/shared/stat-card';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Listbox } from '@/components/ui/listbox';
import { api, type OffsetPage } from '@/lib/api';
import { PERMISSIONS, hasPermission, useCurrentMembership } from '@/lib/permissions';
import { usePreferences } from '@/stores/preferences-store';
import { formatDateTime, formatNumber } from '@/utils/format';
import { formatInsightsMoneySummary, insightsHasMultipleCurrencies } from '@/lib/money-totals';

type IntentStatus =
  | 'created'
  | 'pending'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'expired'
  | 'review_required';

interface IntentInsights {
  currency: string;
  receivedAmountMinor: number;
  receivedCount: number;
  pendingCount: number;
  failedCount: number;
  needsReviewCount: number;
  activeLinkCount: number;
  byCurrency?: Array<{ currency: string; amountMinor: number }>;
}

const STATUS_ANY = '__any_status__';
const METHOD_ANY = '__any_method__';
const FUND_ANY = '__any_fund__';

interface FundOption {
  id: string;
  name: string;
}

const VIEW_STATUSES: Array<typeof STATUS_ANY | IntentStatus> = [
  'succeeded',
  STATUS_ANY,
  'failed',
  'pending',
  'review_required',
];
const METHODS = ['mobile_money', 'card'] as const;

const statusBadgeVariant = (status: IntentStatus): BadgeVariant => {
  if (status === 'succeeded') return 'success';
  if (status === 'failed' || status === 'cancelled' || status === 'expired') return 'danger';
  if (status === 'review_required') return 'warning';
  return 'info';
};

interface OnlineAttemptsPanelProps {
  /** When set, only gifts for this contribution type are listed (e.g. tithe). */
  contributionType?: string;
  /** Locale prefix under payments — `attempts` or `tithes`. */
  labelsKey: 'attempts' | 'tithes';
  /** Whether to show link-related insight stats (gifts tab only). */
  showLinkInsights?: boolean;
}

export const OnlineAttemptsPanel = ({
  contributionType,
  labelsKey,
  showLinkInsights = false,
}: OnlineAttemptsPanelProps) => {
  const { t } = useTranslation('payments');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const timezone = usePreferences((state) => state.timezone);
  const membership = useCurrentMembership();
  const queryClient = useQueryClient();
  const canRecheck = hasPermission(membership, PERMISSIONS.PAYMENTS_RECONCILE);
  const canReconcile = canRecheck;
  const canRecordGift = hasPermission(membership, PERMISSIONS.GIVING_RECORD);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<typeof STATUS_ANY | IntentStatus>('succeeded');
  const [filterMethod, setFilterMethod] = useState(METHOD_ANY);
  const [filterFundId, setFilterFundId] = useState(FUND_ANY);
  const [filterDateRange, setFilterDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));
  const [insightsDateRange, setInsightsDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));
  const [detailIntent, setDetailIntent] = useState<PaymentIntentDetail | null>(null);

  const appliedCount = [
    search.trim() ? search : null,
    filterStatus !== 'succeeded' ? filterStatus : null,
    filterMethod === METHOD_ANY ? null : filterMethod,
    filterFundId === FUND_ANY ? null : filterFundId,
    filterDateRange.preset !== 'thisMonth' ? filterDateRange.preset : null,
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearch('');
    setFilterStatus('succeeded');
    setFilterMethod(METHOD_ANY);
    setFilterFundId(FUND_ANY);
    setFilterDateRange({
      preset: 'thisMonth',
      ...resolvePreset('thisMonth', timezone),
    });
    setPage(1);
  };

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'filter'],
    queryFn: () =>
      api.get<{ data: FundOption[] }>('/giving/funds', {
        query: { page: 1, pageSize: 100 },
      }),
  });

  const list = useQuery({
    queryKey: [
      'payments',
      'intents',
      labelsKey,
      {
        page,
        pageSize,
        search,
        status: filterStatus,
        method: filterMethod,
        fundId: filterFundId,
        from: filterDateRange.from,
        to: filterDateRange.to,
        contributionType,
      },
    ],
    queryFn: () =>
      api.get<{ data: PaymentIntentDetail[]; page: OffsetPage }>('/payments/intents', {
        query: {
          page,
          pageSize,
          q: search.trim() || undefined,
          ...(filterStatus === STATUS_ANY ? {} : { status: filterStatus }),
          ...(filterMethod === METHOD_ANY ? {} : { method: filterMethod }),
          ...(filterFundId === FUND_ANY ? {} : { fundId: filterFundId }),
          dateFrom: filterDateRange.from ?? undefined,
          dateTo: filterDateRange.to ?? undefined,
          ...(contributionType ? { contributionType } : {}),
        },
      }),
    placeholderData: keepPreviousData,
  });

  const insights = useQuery({
    queryKey: [
      'payments',
      'intents',
      'insights',
      labelsKey,
      insightsDateRange.from,
      insightsDateRange.to,
      contributionType,
    ],
    queryFn: () =>
      api.get<IntentInsights>('/payments/intents/insights', {
        query: {
          dateFrom: insightsDateRange.from ?? undefined,
          dateTo: insightsDateRange.to ?? undefined,
          ...(contributionType ? { contributionType } : {}),
        },
      }),
  });

  const columns: DataTableColumn<PaymentIntentDetail>[] = useMemo(
    () => [
      {
        id: 'date',
        header: t(`${labelsKey}.columns.date`),
        cell: (row) => formatDateTime(row.completedAt ?? row.createdAt),
      },
      {
        id: 'amount',
        header: t(`${labelsKey}.columns.amount`),
        money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
      },
      {
        id: 'fund',
        header: t(`${labelsKey}.columns.fund`),
        cell: (row) => row.fundName?.trim() || '—',
      },
      {
        id: 'status',
        header: t(`${labelsKey}.columns.status`),
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusBadgeVariant(row.status)}>
              {t(`attempts.status.${row.status}`)}
            </Badge>
            {row.isTest ? (
              <Badge variant="neutral">{t('attempts.testBadge')}</Badge>
            ) : null}
          </div>
        ),
      },
      {
        id: 'method',
        header: t(`${labelsKey}.columns.method`),
        cell: (row) => (row.method ? t(`attempts.method.${row.method}`) : '—'),
        hideBelowMd: true,
      },
      {
        id: 'giver',
        header: t(`${labelsKey}.columns.giver`),
        cell: (row) => row.giverName?.trim() || '—',
        hideBelowMd: true,
      },
      {
        id: 'outcome',
        header: t(`${labelsKey}.columns.outcome`),
        cell: (row) => {
          if (row.contributionId) {
            return (
              <Link
                to={`/giving/contributions/${row.contributionId}`}
                className="text-body-sm font-medium text-accent hover:underline"
                onClick={(event) => event.stopPropagation()}
              >
                {t(`${labelsKey}.viewContribution`)}
              </Link>
            );
          }
          if (row.failureCode) {
            return (
              <span className="text-body-sm text-danger">
                {tErrors(row.failureCode === 'UNKNOWN' ? 'PAYMENT_FAILED_UNKNOWN' : row.failureCode)}
              </span>
            );
          }
          return '—';
        },
      },
    ],
    [labelsKey, t, tErrors],
  );

  const viewStatusOptions = useMemo(
    () =>
      VIEW_STATUSES.map((status) => ({
        value: status,
        label:
          status === 'succeeded'
            ? t(`${labelsKey}.filters.showReceived`)
            : status === STATUS_ANY
              ? t(`${labelsKey}.filters.showAll`)
              : t(`attempts.status.${status}`),
      })),
    [labelsKey, t],
  );

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;

  const dateLabels = {
    today: tCommon('date.today'),
    yesterday: tCommon('date.yesterday'),
    thisWeek: tCommon('date.thisWeek'),
    lastWeek: tCommon('date.lastWeek'),
    thisMonth: tCommon('date.thisMonth'),
    lastMonth: tCommon('date.lastMonth'),
    thisQuarter: tCommon('date.thisQuarter'),
    thisYear: tCommon('date.thisYear'),
    lastYear: tCommon('date.lastYear'),
    allTime: tCommon('date.allTime'),
    custom: tCommon('date.custom'),
    from: t(`${labelsKey}.filters.dateFrom`),
    to: t(`${labelsKey}.filters.dateTo`),
  };

  return (
    <>
      <CollapsibleInsights
        summary={[
          {
            label: t(`${labelsKey}.insights.received`),
            value: insights.data
              ? formatInsightsMoneySummary(
                  {
                    amountMinor: insights.data.receivedAmountMinor,
                    currency: insights.data.currency,
                    count: insights.data.receivedCount,
                    byCurrency: insights.data.byCurrency,
                  },
                  insights.data.currency,
                )
              : '…',
          },
          {
            label: t(`${labelsKey}.insights.gifts`),
            value: insights.data ? formatNumber(insights.data.receivedCount) : '…',
          },
        ]}
      >
        <DateRangeFilter
          value={insightsDateRange}
          onChange={setInsightsDateRange}
          labels={dateLabels}
          presets={LIST_FILTER_DATE_RANGE_PRESETS}
        />

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {insightsHasMultipleCurrencies(insights.data) ? (
            insights.data!.byCurrency!.map((item) => (
              <StatCard
                key={item.currency}
                label={`${t(`${labelsKey}.insights.received`)} (${item.currency})`}
                money={{ amount: item.amountMinor, currency: item.currency }}
                icon={<HandCoins className="size-4" aria-hidden />}
                loading={insights.isLoading}
              />
            ))
          ) : (
            <StatCard
              label={t(`${labelsKey}.insights.received`)}
              money={
                insights.data
                  ? {
                      amount: insights.data.receivedAmountMinor,
                      currency: insights.data.currency,
                    }
                  : undefined
              }
              icon={<HandCoins className="size-4" aria-hidden />}
              loading={insights.isLoading}
            />
          )}
          <StatCard
            label={t(`${labelsKey}.insights.gifts`)}
            value={insights.data ? formatNumber(insights.data.receivedCount) : undefined}
            icon={<Gift className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t(`${labelsKey}.insights.pending`)}
            value={insights.data ? formatNumber(insights.data.pendingCount) : undefined}
            icon={<Clock3 className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t(`${labelsKey}.insights.failed`)}
            value={insights.data ? formatNumber(insights.data.failedCount) : undefined}
            icon={<AlertTriangle className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>

        {showLinkInsights ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard
              label={t('attempts.insights.needsReview')}
              value={insights.data ? formatNumber(insights.data.needsReviewCount) : undefined}
              icon={<AlertTriangle className="size-4" aria-hidden />}
              loading={insights.isLoading}
            />
            <StatCard
              label={t('attempts.insights.activeLinks')}
              value={insights.data ? formatNumber(insights.data.activeLinkCount) : undefined}
              icon={<Link2 className="size-4" aria-hidden />}
              loading={insights.isLoading}
            />
          </div>
        ) : (
          <StatCard
            label={t(`${labelsKey}.insights.needsReview`)}
            value={insights.data ? formatNumber(insights.data.needsReviewCount) : undefined}
            icon={<AlertTriangle className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        )}
      </CollapsibleInsights>

      <FilterBar
        appliedCount={appliedCount}
        onClearAll={resetFilters}
        labels={{
          filters: t(`${labelsKey}.filters.title`),
          clearAll: t(`${labelsKey}.filters.clearAll`),
          applied: (count) => t(`${labelsKey}.filters.applied`, { count }),
        }}
        leading={
          <div className="flex flex-wrap items-end gap-2">
            <SearchInput
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              placeholder={t(`${labelsKey}.filters.search`)}
              clearLabel={tCommon('search.clear')}
              className="max-w-none sm:w-56"
              aria-label={t(`${labelsKey}.filters.search`)}
            />
            <div className="w-48 min-w-[11rem]">
              <Listbox
                value={filterStatus}
                onChange={(value) => {
                  setFilterStatus(value ?? 'succeeded');
                  setPage(1);
                }}
                options={viewStatusOptions}
                aria-label={t(`${labelsKey}.filters.show`)}
              />
            </div>
          </div>
        }
      >
        {({ popoverPortal }) => (
          <>
            <div className="w-44 min-w-[10rem]">
              <Listbox
                value={filterMethod}
                onChange={(value) => {
                  setFilterMethod(value ?? METHOD_ANY);
                  setPage(1);
                }}
                options={[
                  { value: METHOD_ANY, label: t(`${labelsKey}.filters.methodAny`) },
                  ...METHODS.map((method) => ({
                    value: method,
                    label: t(`attempts.method.${method}`),
                  })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="w-48 min-w-[11rem]">
              <Listbox
                value={filterFundId}
                onChange={(value) => {
                  setFilterFundId(value ?? FUND_ANY);
                  setPage(1);
                }}
                options={[
                  { value: FUND_ANY, label: t(`${labelsKey}.filters.fundAny`) },
                  ...(funds.data?.data ?? []).map((fund) => ({
                    value: fund.id,
                    label: fund.name,
                  })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <DateRangeFilter
              value={filterDateRange}
              onChange={(range) => {
                setFilterDateRange(range);
                setPage(1);
              }}
              popoverPortal={popoverPortal}
              labels={dateLabels}
              presets={LIST_FILTER_DATE_RANGE_PRESETS}
            />
          </>
        )}
      </FilterBar>

      {list.isError ? (
        <div className="mt-6 space-y-3 rounded-lg border border-border px-4 py-8 text-center">
          <p className="text-body-sm text-fg-secondary">{t(`${labelsKey}.error`)}</p>
          <div className="flex justify-end sm:justify-center">
            <Button variant="secondary" onClick={() => void list.refetch()}>
              {t(`${labelsKey}.retry`)}
            </Button>
          </div>
        </div>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={list.isLoading}
          empty={
            <p className="text-center text-body-sm text-fg-secondary">
              {appliedCount > 0 ? t(`${labelsKey}.emptyFiltered`) : t(`${labelsKey}.empty`)}
            </p>
          }
          onRowClick={(row) => setDetailIntent(row)}
        />
      )}

      {pageMeta && pageMeta.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <PaymentIntentDetailDialog
        intent={detailIntent}
        open={Boolean(detailIntent)}
        canRecheck={canRecheck}
        canReconcile={canReconcile}
        canRecordGift={canRecordGift}
        onIntentUpdated={setDetailIntent}
        onRecheckSuccess={() => {
          void queryClient.invalidateQueries({ queryKey: ['payments', 'intents'] });
        }}
        onOpenChange={(open) => {
          if (!open) setDetailIntent(null);
        }}
      />
    </>
  );
};
